import React, { useState, useEffect } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer, Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Wallet, TrendingUp, TrendingDown } from 'lucide-react';
import { timeTrackingAPI } from '../../lib/api';

const fmt = (v) => {
  const n = Number(v || 0);
  return `${n > 0 ? '+' : ''}${n.toFixed(2).replace('.', ',')}`;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="rounded-lg border bg-background px-3 py-2 shadow-md text-xs">
      <p className="font-semibold mb-1">{label}</p>
      <p className={row.month_overtime >= 0 ? 'text-green-600' : 'text-red-600'}>
        Monat: {fmt(row.month_overtime)} Std
      </p>
      <p className={row.cumulative_balance >= 0 ? 'text-green-600' : 'text-red-600'}>
        Gesamtsaldo: {fmt(row.cumulative_balance)} Std
      </p>
    </div>
  );
};

export const MyBalanceTrendCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await timeTrackingAPI.getMyBalanceTrend(6);
        if (active) setData(res.data);
      } catch {
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) return <Skeleton className="h-[280px] rounded-xl" />;
  if (!data?.enabled || !data?.trend?.length) return null;

  const current = data.current_balance || 0;
  const positive = current >= 0;

  return (
    <Card className="border-border/60" data-testid="my-balance-trend-card">
      <CardHeader className="px-3 sm:px-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <CardTitle className="font-heading text-lg sm:text-xl flex items-center gap-2">
              <Wallet className="h-5 w-5 text-primary" />
              Mein Stundenkonto
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Verlauf der letzten 6 Monate
            </CardDescription>
          </div>
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-xl ${positive ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20'}`}
            data-testid="my-balance-current"
          >
            {positive ? <TrendingUp className="h-5 w-5 text-green-600" /> : <TrendingDown className="h-5 w-5 text-red-600" />}
            <div className="text-right">
              <p className="text-[11px] text-muted-foreground leading-none">Aktueller Saldo</p>
              <p className={`text-xl font-bold font-mono leading-tight ${positive ? 'text-green-600' : 'text-red-600'}`}>
                {fmt(current)} <span className="text-sm">Std</span>
              </p>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-1 sm:px-6">
        <div className="h-[220px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={data.trend} margin={{ top: 10, right: 8, left: -16, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
              <Bar dataKey="month_overtime" name="Monat" radius={[4, 4, 0, 0]} barSize={26}>
                {data.trend.map((d) => (
                  <Cell key={`${d.year}-${d.month}`} fill={d.month_overtime >= 0 ? '#22c55e' : '#ef4444'} />
                ))}
              </Bar>
              <Line
                type="monotone"
                dataKey="cumulative_balance"
                name="Gesamtsaldo"
                stroke="#6366f1"
                strokeWidth={2.5}
                dot={{ r: 3, fill: '#6366f1' }}
                activeDot={{ r: 5 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center justify-center gap-4 mt-2 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-green-500" /> Plus-Monat</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2 w-3 rounded-sm bg-red-500" /> Minus-Monat</span>
          <span className="flex items-center gap-1"><span className="inline-block h-0.5 w-4 bg-indigo-500" /> Gesamtsaldo</span>
        </div>
      </CardContent>
    </Card>
  );
};
